import { apiClient } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Types
export interface DayBooking {
  _id: string;
  classId: string | { _id: string; name: string; label: string };
  timeSlotId: string;
  userId: string;
  startTime: string;
  endTime: string;
  date: string;
  fullName: string;
  phoneNumber?: string;
  status: 'confirmed' | 'cancelled' | 'pending' | 'active';
  classLabel: string;
  className: string;
  createdAt: string;
  updatedAt: string;
  staffMember?: {
    _id: string;
    name: string;
    surname: string;
    userType: string;
  };
}

export interface StaffMember {
  _id: string;
  name: string;
  surname: string;
  userType: string;
}

export interface TimeSlot {
  _id?: string;
  timeSlotId: string;
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
  maxParticipants: number;
  disabled?: boolean;
  applyEveryDay?: boolean; // true = recurs daily, false = specific date only
  date?: string; // YYYY-MM-DD format (required when applyEveryDay=false)
  disabledDates?: string[]; // YYYY-MM-DD array for one-off disabled days
  seriesId?: string;
  bookedCount?: number;
  availableSpots?: number;
  isFull?: boolean;
  daysOfWeek?: number[]; // Days when slot is active (0=Sunday, 6=Saturday)
  staffMember?: StaffMember; // Assigned instructor
}

export interface GymClass {
  _id: string;
  id?: string;
  name: string;
  label: string;
  timeSlots: TimeSlot[];
  isActive: boolean;
  staffMember?: string;
  daysOfWeek?: number[];
  blackoutDates?: string[];
  image?: string;
}

export interface ClassBooking {
  _id: string;
  id?: string;
  classId: string;
  timeSlotId: string;
  date: string; // YYYY-MM-DD format
  fullName: string;
  phoneNumber?: string;
  status: 'active' | 'cancelled';
  cancelledAt?: string;
  createdAt?: string;
}

// API Functions
export const createClass = async (data: {
  name: string;
  label: string;
  staffMember?: string;
  daysOfWeek?: number[];
}): Promise<GymClass> => {
  const response = await apiClient.post('/classes', data);
  return response.data;
};

export const addTimeSlots = async (
  classId: string,
  data: {
    seriesId?: string;
    slots: Array<{
      startTime: string; // HH:mm format
      endTime: string; // HH:mm format
      maxParticipants: number;
      disabled?: boolean;
      applyEveryDay?: boolean; // true for daily recurrence
      date?: string; // YYYY-MM-DD (required when applyEveryDay=false)
      staffMember?: string; // MongoDB ObjectId
      daysOfWeek?: number[]; // Days of week (0-6)
    }>;
  }
): Promise<GymClass> => {
  const response = await apiClient.post(`/classes/${classId}/timeslots`, data);
  return response.data;
};

export const updateTimeSlot = async (
  classId: string,
  timeSlotId: string,
  data: {
    startTime?: string; // HH:mm format
    endTime?: string; // HH:mm format
    maxParticipants?: number;
    disabled?: boolean;
    applyEveryDay?: boolean;
    date?: string; // YYYY-MM-DD
    seriesId?: string;
    disableDate?: string; // YYYY-MM-DD to add to disabledDates array
    staffMember?: string; // MongoDB ObjectId
    daysOfWeek?: number[]; // Days of week (0-6)
  }
): Promise<GymClass> => {
  const response = await apiClient.patch(
    `/classes/${classId}/timeslots/${timeSlotId}`,
    data
  );
  return response.data;
};

export const getClasses = async (): Promise<GymClass[]> => {
  const response = await apiClient.get('/classes');
  return response.data;
};

export const getClass = async (classId: string): Promise<GymClass> => {
  const response = await apiClient.get(`/classes/${classId}`);
  return response.data;
};

export const updateClass = async (
  classId: string,
  data: {
    name?: string;
    label?: string;
    isActive?: boolean;
    staffMember?: string;
    daysOfWeek?: number[];
    blackoutDates?: string[];
    addBlackoutDate?: string;
    removeBlackoutDate?: string;
  }
): Promise<GymClass> => {
  const response = await apiClient.patch(`/classes/${classId}`, data);
  return response.data;
};

export const getTimeSlots = async (
  classId: string,
  params?: {
    date?: string; // YYYY-MM-DD format
    includeFull?: boolean;
    includeDisabled?: boolean;
  }
): Promise<TimeSlot[]> => {
  const response = await apiClient.get(`/classes/${classId}/timeslots`, {
    params,
  });
  return response.data;
};

// Get all slots across all classes for a given day
export const getAllSlots = async (
  date: string // YYYY-MM-DD format
): Promise<
  (TimeSlot & { class: { _id: string; name: string; label: string } })[]
> => {
  const response = await apiClient.get('/classes/slots', {
    params: { date },
  });
  return response.data;
};

export const createBooking = async (
  classId: string,
  timeSlotId: string,
  data: {
    fullName: string;
    phoneNumber?: string;
    date: string; // YYYY-MM-DD format (required)
  }
): Promise<ClassBooking> => {
  const response = await apiClient.post(
    `/classes/${classId}/timeslots/${timeSlotId}/bookings`,
    data
  );
  return response.data;
};

export const getBookings = async (
  classId: string,
  timeSlotId?: string,
  date?: string // YYYY-MM-DD
): Promise<ClassBooking[]> => {
  const params: any = {};
  if (timeSlotId) params.timeSlotId = timeSlotId;
  if (date) params.date = date;

  const response = await apiClient.get(`/classes/${classId}/bookings`, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return response.data;
};

export const cancelBooking = async (
  classId: string,
  bookingId: string
): Promise<ClassBooking> => {
  const response = await apiClient.patch(
    `/classes/${classId}/bookings/${bookingId}/cancel`
  );
  return response.data;
};

// React Query Hooks
export const useClasses = () => {
  return useQuery({
    queryKey: ['classes'],
    queryFn: getClasses,
  });
};

export const useClass = (classId: string) => {
  return useQuery({
    queryKey: ['classes', classId],
    queryFn: () => getClass(classId),
    enabled: !!classId,
  });
};

export const useTimeSlots = (
  classId: string,
  params?: {
    date?: string; // YYYY-MM-DD format
    includeFull?: boolean;
    includeDisabled?: boolean;
  }
) => {
  return useQuery({
    queryKey: ['classes', classId, 'timeslots', params],
    queryFn: () => getTimeSlots(classId, params),
    enabled: !!classId,
  });
};

export const useAllSlots = (date: string) => {
  return useQuery({
    queryKey: ['classes', 'slots', date],
    queryFn: () => getAllSlots(date),
    enabled: !!date,
  });
};

export const useBookings = (
  classId: string,
  timeSlotId?: string,
  date?: string
) => {
  return useQuery({
    queryKey: ['classes', classId, 'bookings', timeSlotId, date],
    queryFn: () => getBookings(classId, timeSlotId, date),
    enabled: !!classId,
  });
};

export const useCreateClass = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createClass,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classes'] });
    },
  });
};

export const useAddTimeSlots = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      classId,
      data,
    }: {
      classId: string;
      data: {
        seriesId?: string;
        slots: Array<{
          startTime: string; // HH:mm format
          endTime: string; // HH:mm format
          maxParticipants: number;
          disabled?: boolean;
          applyEveryDay?: boolean;
          date?: string; // YYYY-MM-DD
          staffMember?: string;
          daysOfWeek?: number[];
        }>;
      };
    }) => addTimeSlots(classId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({
        queryKey: ['classes', variables.classId],
      });
    },
  });
};

export const useUpdateTimeSlot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      classId,
      timeSlotId,
      data,
    }: {
      classId: string;
      timeSlotId: string;
      data: {
        startTime?: string; // HH:mm format
        endTime?: string; // HH:mm format
        maxParticipants?: number;
        disabled?: boolean;
        applyEveryDay?: boolean;
        date?: string; // YYYY-MM-DD
        seriesId?: string;
        disableDate?: string; // YYYY-MM-DD
        staffMember?: string;
        daysOfWeek?: number[];
      };
    }) => updateTimeSlot(classId, timeSlotId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({
        queryKey: ['classes', variables.classId],
      });
    },
  });
};

export const useCreateBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      classId,
      timeSlotId,
      data,
    }: {
      classId: string;
      timeSlotId: string;
      data: {
        fullName: string;
        phoneNumber?: string;
        date: string; // YYYY-MM-DD format (required)
      };
    }) => createBooking(classId, timeSlotId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['classes', variables.classId, 'timeslots'],
      });
      queryClient.invalidateQueries({
        queryKey: ['classes', variables.classId, 'bookings'],
      });
    },
  });
};

export const fetchBookingsByDay = async (date: string): Promise<DayBooking[]> => {
  const response = await apiClient.get('/classes/bookings/day', { params: { date } });
  return response.data;
};

export const useBookingsByDay = (date: string) => {
  return useQuery({
    queryKey: ['classes', 'bookings', 'day', date],
    queryFn: () => fetchBookingsByDay(date),
    enabled: !!date,
  });
};

// Eligible users for a specific class (admin)
export interface EligibleUser {
  _id: string;
  name: string;
  surname: string;
  email: string;
  phone?: string;
  remainingEntries: number | null;
}

export const fetchEligibleUsers = async (classId: string): Promise<EligibleUser[]> => {
  const response = await apiClient.get(`/classes/${classId}/eligible-users`);
  return response.data;
};

export const useEligibleUsers = (classId: string) => {
  return useQuery({
    queryKey: ['classes', classId, 'eligible-users'],
    queryFn: () => fetchEligibleUsers(classId),
    enabled: !!classId,
  });
};

// Admin book user into a class
export const adminBookUser = async (
  classId: string,
  timeSlotId: string,
  data: { userId: string; fullName: string; phoneNumber?: string; date: string },
): Promise<ClassBooking> => {
  const response = await apiClient.post(
    `/classes/${classId}/timeslots/${timeSlotId}/admin-book`,
    data,
  );
  return response.data;
};

export const useAdminBookUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      classId,
      timeSlotId,
      data,
    }: {
      classId: string;
      timeSlotId: string;
      data: { userId: string; fullName: string; phoneNumber?: string; date: string };
    }) => adminBookUser(classId, timeSlotId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['classes', 'bookings', 'day'],
      });
      queryClient.invalidateQueries({
        queryKey: ['classes', variables.classId, 'bookings'],
      });
      queryClient.invalidateQueries({
        queryKey: ['classes', variables.classId, 'timeslots'],
      });
      queryClient.invalidateQueries({
        queryKey: ['classes', variables.classId, 'eligible-users'],
      });
    },
  });
};

export const useCancelBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      classId,
      bookingId,
    }: {
      classId: string;
      bookingId: string;
    }) => cancelBooking(classId, bookingId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['classes', variables.classId, 'timeslots'],
      });
      queryClient.invalidateQueries({
        queryKey: ['classes', variables.classId, 'bookings'],
      });
    },
  });
};

export const uploadClassImage = async (
  classId: string,
  imageBase64: string,
): Promise<{ url: string }> => {
  const response = await apiClient.patch(`/classes/${classId}/image`, {
    image: imageBase64,
  });
  return response.data;
};

export const useUploadClassImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ classId, image }: { classId: string; image: string }) =>
      uploadClassImage(classId, image),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['classes'] });
    },
  });
};

export const useUpdateClass = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      classId,
      data,
    }: {
      classId: string;
      data: {
        name?: string;
        label?: string;
        isActive?: boolean;
        staffMember?: string;
        daysOfWeek?: number[];
        blackoutDates?: string[];
        addBlackoutDate?: string;
        removeBlackoutDate?: string;
      };
    }) => updateClass(classId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({
        queryKey: ['classes', variables.classId],
      });
    },
  });
};
